import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import { resolveApiMessage, showToast } from '../../../helpers';
import {
  setReservationStage,
  TReservationStage,
} from '../../../requests/opsScheduling';
import { btnSecondary, Card, inputClass, labelClass } from '../ui';

/** Reservation stage — the portal's replacement for the GoMeddo status ladder. */
const ReservationStageCard: React.FC<{
  id: string;
  stage: TReservationStage;
  refetchStage: () => unknown;
}> = ({ id, stage, refetchStage }) => {
  const queryClient = useQueryClient();
  const [stagePick, setStagePick] = useState('');
  const [stageNote, setStageNote] = useState('');
  const [holdDays, setHoldDays] = useState('');
  const stageSet = useMutation({
    mutationFn: () =>
      setReservationStage(id!, {
        status: stagePick,
        holdDays:
          stagePick === 'Hold' && holdDays ? Number(holdDays) : undefined,
        note: stageNote || null,
      }),
    onSuccess: (r) => {
      showToast(
        r.status === 'Hold' && r.holdExpiresAt
          ? `On hold until ${r.holdExpiresAt.slice(0, 10)}`
          : r.status === 'Fully Confirmed'
          ? 'Fully confirmed — client confirmation set'
          : `Reservation: ${r.status}`,
        'success',
      );
      setStagePick('');
      setStageNote('');
      refetchStage();
      queryClient.invalidateQueries({ queryKey: ['opsClass', id] });
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  return (
    <Card title={`Reservation · ${stage.status ?? 'no stage yet'}`}>
      <div className="px-5 py-4 text-sm">
        <div className="text-xs text-gray-bold">
          {stage.basis === 'portal'
            ? `Set here ${
                stage.setAt ? String(stage.setAt).slice(0, 10) : ''
              } by ${stage.setBy ?? ''}`
            : stage.basis === 'reservation'
            ? 'Read from the GoMeddo reservation until a stage is set here'
            : 'No reservation stage yet — place a hold or move it along'}
          {stage.holdExpiresAt && stage.status === 'Hold' && (
            <>
              {' · '}
              <span className={stage.holdExpired ? 'text-red-600' : ''}>
                hold {stage.holdExpired ? 'expired' : 'until'}{' '}
                {String(stage.holdExpiresAt).slice(0, 16).replace('T', ' ')}
              </span>
            </>
          )}
          {stage.note ? ` · ${stage.note}` : ''}
        </div>
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <div>
            <label className={labelClass}>Move to</label>
            <select
              className={inputClass}
              value={stagePick}
              onChange={(e) => setStagePick(e.target.value)}
            >
              <option value="">Choose a stage</option>
              {stage.settable.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          {stagePick === 'Hold' && (
            <div>
              <label className={labelClass}>Hold days</label>
              <input
                className={`${inputClass} w-24`}
                type="number"
                min={1}
                max={90}
                placeholder="default"
                value={holdDays}
                onChange={(e) => setHoldDays(e.target.value)}
              />
            </div>
          )}
          <div className="min-w-[200px] flex-1">
            <label className={labelClass}>Note</label>
            <input
              className={`${inputClass} w-full`}
              value={stageNote}
              onChange={(e) => setStageNote(e.target.value)}
              placeholder="e.g. waiting on Joe to confirm"
            />
          </div>
          <button
            type="button"
            className={btnSecondary}
            disabled={!stagePick || stageSet.isPending}
            onClick={() => stageSet.mutate()}
          >
            Set stage
          </button>
        </div>
        {stage.history.length > 0 && (
          <ul className="mt-3 text-xs text-gray-bold">
            {stage.history.slice(0, 6).map((h, i) => (
              <li key={i}>
                {String(h.at).slice(0, 16).replace('T', ' ')} · {h.from ?? '—'}{' '}
                → {h.to} · {h.by}
                {h.note ? ` · ${h.note}` : ''}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-2 text-[11px] text-gray-bold">
          Fully Confirmed also ticks client confirmed. Cancelling the
          reservation does not cancel the class — that is the class status
          above.
        </div>
      </div>
    </Card>
  );
};

export default ReservationStageCard;
